// ============================================================================
// sync-folder-manifest.js — DEC-COUNCIL-007 / Candidate C5 Slice 1
// ----------------------------------------------------------------------------
// Single owner of the SyncManifest.json v4 schema. Extracted from sync-folder.js
// so the schema-version constant, the dataFiles registry, and the keyFields
// extraction are concentrated in one place that:
//
//   1. Bumping the schema version is a one-line change (was: a constant
//      hardcoded inside a 600-line method body).
//   2. Adding a new top-level data file requires extending DATA_FILES_REGISTRY
//      so the manifest writer + the restore path stay in lockstep. Previously
//      a forgotten push to dataFiles[] meant the restore path silently
//      dropped the file.
//   3. Snapshot tests on `buildSyncManifestV4()` lock the on-disk shape — see
//      src/__tests__/sync-folder-manifest.unit.test.js.
//
// What this module is NOT:
//   - It does NOT write to disk. The orchestrator (SyncFolderService) is the
//     I/O boundary; this is a pure data shape.
//   - It does NOT defend invariants beyond schema version (callers may still
//     pass partial data; the manifest is built from what's provided).
//
// See:
//   - docs/council/DECISION-LOG.md DEC-COUNCIL-007 Candidate C5 Slice 1
//   - CONTEXT.md → "Sync Folder Manifest Writer"
// ============================================================================

/**
 * Current schema version. Bump only in this file when the manifest shape
 * changes. The schema-drift-detector + the restore path read this number.
 *
 * v5 (2026-05-20, sync-folder de-bloat): originals + extracted-text sidecars
 * + transcripts + nested learnings/ subfolder no longer written. Manifest
 * `documents[].fileName` is still emitted (so the manifest writer's shape
 * is unchanged), but in practice it falls back to the derived name for
 * back-compat — no original PDF is on disk to read it from. AI_Learnings.json
 * is now at project root (was learnings/AI_Learnings.json).
 *
 * Back-compat: readers handle v4 manifests with the old layout cleanly.
 * See sync-folder.js header comment for the full v5 folder tree.
 */
export const SYNC_MANIFEST_SCHEMA_VERSION = 5;

/**
 * Naming convention string surfaced in the manifest for human + tool readers.
 * Coordinated with sync-folder-naming.js conventions.
 */
export const SYNC_MANIFEST_NAMING_CONVENTION = 'YYYY-MM-DD - QR ref - Requester - SP - Site address - ProjectId';

/**
 * Registry of optional top-level data files. Each entry says: "if results.<key>
 * is truthy, include <filename> in the manifest's dataFiles[] list."
 *
 * Adding a new top-level file = adding one row here. The writer + the restore
 * path read from the same list, so forgetting to update one side is not
 * possible.
 */
export const DATA_FILES_REGISTRY = Object.freeze([
    { resultKey: 'projectIndex', fileName: 'Project_Index.md' },
    { resultKey: 'notes',        fileName: 'Inspector_Notes.txt' },
    { resultKey: 'riskReport',   fileName: 'Risk_Report.txt' },
    { resultKey: 'learnings',    fileName: 'AI_Learnings.json' },
]);

/**
 * The two unconditional data files every manifest carries.
 */
const ALWAYS_INCLUDED_DATA_FILES = Object.freeze(['QuoteData.json', 'SyncManifest.json']);

/**
 * Pull the canonical CRM identifiers off a project record, tolerating both
 * camelCase and snake_case forms (the project envelope has historically
 * lived in both shapes across migration waves). Defence-in-depth against
 * a regression to one form starving a feature reader.
 */
function extractCanonicalIdentifiers(projectRecord: any = {}) {
    const companyId = projectRecord.companyId
        || projectRecord.company_id
        || projectRecord.formData?.clientCompanyId
        || projectRecord.formData?.companyId
        || projectRecord.formData?.client_id
        || null;
    const propertyId = projectRecord.propertyId
        || projectRecord.property_id
        || null;
    const contactId = projectRecord.contactId
        || projectRecord.contact_id
        || projectRecord.formData?.contactId
        || projectRecord.formData?.contact_id
        || null;
    return { companyId, propertyId, contactId };
}

/**
 * Build the keyFields block — pivot identifiers + project state. v4 schema
 * promoted companyId/propertyId/contactId from formData-only to first-class
 * keyFields so cross-surface joins (CRM workbench / Project history / Batch
 * processor) don't need to re-extract.
 */
function buildKeyFields(projectRecord: any = {}, identifiers: any) {
    const formData = projectRecord.formData || {};
    return {
        quoteRef: formData.orderNumber || projectRecord.quoteNumber || '',
        requesterCompany: formData.requesterCompany || formData.clientCompany || '',
        contact: formData.clientName || '',
        siteAddress: formData.address || projectRecord.address || '',
        strataPlan: formData.strataplan || formData.strataPlan || '',
        status: projectRecord.status || '',
        currentStage: projectRecord.currentStage || null,
        companyId: identifiers.companyId,
        propertyId: identifiers.propertyId,
        contactId: identifiers.contactId,
    };
}

/**
 * Build the dataFiles[] list from the registry + always-included files.
 * results is the boolean-keyed object the orchestrator tracks: { projectIndex:
 * true, notes: false, riskReport: true, learnings: true }.
 */
function buildDataFilesList(results: any = {}) {
    const optional = DATA_FILES_REGISTRY.filter(entry => results[entry.resultKey]).map(entry => entry.fileName);
    return [...ALWAYS_INCLUDED_DATA_FILES, ...optional];
}

/**
 * Build a v4 SyncManifest object from the orchestrator's gathered data.
 *
 * Pure: no I/O, no `this` binding, no DataService calls. All inputs are
 * passed in. Returns a plain object ready to JSON.stringify + write.
 *
 * @param {object} args
 * @param {object} args.projectRecord — full project envelope as held by the orchestrator
 * @param {string} args.folderName — current on-disk folder name
 * @param {string} args.desiredFolderName — name the renamer would produce today
 * @param {Array}  args.photos — photo entries (post-portability)
 * @param {Array}  args.documents — document entries
 * @param {Array}  args.videos — video entries
 * @param {Array}  args.documentSyncFileNames — [{ index, syncFileName }]
 * @param {Array}  args.videoSyncFileNames — [{ index, syncFileName, storagePolicy? }]
 * @param {object} args.results — boolean flags for which dataFiles landed
 * @param {Array}  args.photoSyncFailures
 * @param {Array}  args.documentSyncFailures
 * @param {Array}  args.videoSyncFailures
 * @param {Array}  args.dataSyncFailures
 * @param {object} args.helpers — orchestrator-bound formatters
 * @param {(photo, index) => string}        args.helpers.buildPhotoName
 * @param {(doc, index, suffix?) => string} args.helpers.buildDocName
 * @param {(doc) => boolean}                args.helpers.isPdfDocument
 * @param {(video, ctx) => object}          args.helpers.buildVideoStoragePolicy
 * @param {{ LOCAL_SYNC_REFERENCE: string }} args.helpers.VIDEO_ORIGINAL_STORAGE
 * @param {string} [args.lastSyncAt] — defaults to new Date().toISOString()
 * @returns {object} the manifest object ready to serialise
 */
export function buildSyncManifestV4(args: any = {}) {
    const {
        projectRecord = {},
        folderName = '',
        desiredFolderName = '',
        photos = [],
        documents = [],
        videos = [],
        documentSyncFileNames = [],
        videoSyncFileNames = [],
        results = {},
        photoSyncFailures = [],
        documentSyncFailures = [],
        videoSyncFailures = [],
        dataSyncFailures = [],
        helpers = {},
        lastSyncAt = new Date().toISOString(),
    } = args;

    const {
        buildPhotoName,
        buildDocName,
        isPdfDocument,
        buildVideoStoragePolicy,
        VIDEO_ORIGINAL_STORAGE,
    } = helpers;

    if (typeof buildPhotoName !== 'function') throw new Error('buildSyncManifestV4: helpers.buildPhotoName is required');
    if (typeof buildDocName !== 'function') throw new Error('buildSyncManifestV4: helpers.buildDocName is required');
    if (typeof isPdfDocument !== 'function') throw new Error('buildSyncManifestV4: helpers.isPdfDocument is required');
    if (typeof buildVideoStoragePolicy !== 'function') throw new Error('buildSyncManifestV4: helpers.buildVideoStoragePolicy is required');

    const documentFileMap = new Map(documentSyncFileNames.map((d: any) => [d.index, d.syncFileName]));
    const identifiers = extractCanonicalIdentifiers(projectRecord);
    const localSyncReferenceFallback = VIDEO_ORIGINAL_STORAGE?.LOCAL_SYNC_REFERENCE || 'local_sync_reference';

    return {
        version: SYNC_MANIFEST_SCHEMA_VERSION,
        projectId: projectRecord.id || projectRecord.dexieId,
        folderName,
        desiredFolderName,
        displayName: folderName,
        namingConvention: SYNC_MANIFEST_NAMING_CONVENTION,
        lastSyncAt,
        keyFields: buildKeyFields(projectRecord, identifiers),
        photos: photos.map((p: any, i: number) => ({
            index: i,
            fileName: buildPhotoName(p, i),
            blobId: p.blobId,
            sizeBytes: p.sizeKB ? p.sizeKB * 1024 : undefined,
        })),
        documents: documents.map((d: any, i: number) => ({
            index: i,
            fileName: documentFileMap.get(i) || (isPdfDocument(d) ? buildDocName(d, i) + '.pdf' : null),
            originalName: d?.name || d?.originalName || null,
            contentType: d?.contentType || d?.mimeType || (isPdfDocument(d) ? 'application/pdf' : null),
            fileMetadataId: d?.fileMetadataId || null,
            pdfS3Key: d?.pdfS3Key || null,
            extractedTextFile: d.content ? buildDocName(d, i, '_extracted') + '.txt' : null,
            hasExtractedText: !!(d.content),
            hasOriginalFile: documentFileMap.has(i),
            class: d?.class || d?.type || d?.aiClassification || 'unknown',
            confidence: typeof d?.confidence === 'number' ? d.confidence : null,
            evidence: Array.isArray(d?.evidence) ? d.evidence : null,
            classifiedAt: d?.classifiedAt || null,
        })),
        videos: videoSyncFileNames.map((v: any) => {
            const video = videos[v.index] || {};
            const policy = v.storagePolicy || video.storagePolicy || buildVideoStoragePolicy(video, { kind: video.processingMode || 'inline' });
            return {
                index: v.index,
                fileName: v.syncFileName,
                originalName: video.name || video.originalName || null,
                sizeBytes: video.size || undefined,
                sourcePath: video.localSourcePath || policy.sourcePath || null,
                processingMode: video.processingMode || policy.processingMode || null,
                originalStorage: policy.originalStorage || localSyncReferenceFallback,
                indexedDbOriginal: !!policy.persistOriginalInIndexedDb,
                cloudOriginal: !!policy.uploadOriginalToCloud,
                evidenceDerivatives: policy.evidenceDerivatives || [],
                localPlaybackRequired: policy.localPlaybackRequired !== false,
                futureWorker: policy.futureWorker || null,
                hasTranscript: !!video.transcript,
                transcriptFile: video.transcript ? `Video_${v.index + 1}_transcript.txt` : null,
            };
        }),
        dataFiles: buildDataFilesList(results),
        photoSyncFailures,
        documentSyncFailures,
        videoSyncFailures,
        dataSyncFailures,
    };
}

// Test hooks — exported for unit tests so they can exercise internals
// without recreating the whole manifest pipeline.
export const _internals = Object.freeze({
    extractCanonicalIdentifiers,
    buildKeyFields,
    buildDataFilesList,
    ALWAYS_INCLUDED_DATA_FILES,
});
